import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { 
  useAddReviewMutation, 
  useUpdateReviewMutation,
  useGetReviewByIdQuery
} from '../../../redux/apis/endpoints/reviewApi';
import { updateReviewDraft } from '../../../redux/slices/reviewSlice';
import Button from '../../../components/ui/Button';
import FormInput from '../../../components/ui/FormInput';
import ReviewPhotoUploader from './ReviewPhotoUploader';

const SingleItemReviewForm = ({ product, paramProductId, fulfillmentGroupId }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const reviewDraft = useSelector(state => state.review.reviewDraft);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [addReview] = useAddReviewMutation();
  const [updateReview] = useUpdateReviewMutation();

  // Get existing review ID from location state
  const existingReviewId = location.state?.existingReview?.id;

  // Fetch existing review data if needed
  const { 
    data: fetchedReviewData,
    isLoading: isLoadingReview,
    error: fetchReviewError,
    isSuccess: isReviewFetched,
  } = useGetReviewByIdQuery(existingReviewId, { 
    skip: !existingReviewId 
  });

  // Initialize review draft when component mounts
  useEffect(() => {
    const currentFulfillmentGroupId = fulfillmentGroupId || location.state?.fulfillmentGroupId;

    if (location.state || currentFulfillmentGroupId) {
      const existingReview = location.state?.existingReview;
      if (existingReview) {
        dispatch(updateReviewDraft({
          rating: existingReview.rating || 5,
          comment: {
            text: existingReview.comment || '',
            pros: existingReview.pros || [],
            cons: existingReview.cons || []
          },
          pictures: existingReview.pictures || [],
          orderId: location.state?.orderId || null,
          merchantId: location.state?.merchantId || null,
          fulfillmentGroupId: currentFulfillmentGroupId || null,
          orderItemId: location.state?.orderItemId || null,
          reviewId: existingReview.id || null
        }));
      } else {
        dispatch(updateReviewDraft({
          rating: 5,
          comment: {
            text: '',
            pros: [],
            cons: []
          },
          pictures: [],
          orderId: location.state?.orderId || null,
          merchantId: location.state?.merchantId || null,
          fulfillmentGroupId: currentFulfillmentGroupId || null,
          orderItemId: location.state?.orderItemId || null
        }));
      }
    }
  }, [dispatch, location.state, fulfillmentGroupId]);

  // Handle rating change
  const handleRatingChange = useCallback((newRating) => {
    dispatch(updateReviewDraft({ rating: newRating }));
  }, [dispatch]);

  // Handle text input change
  const handleCommentChange = useCallback((e) => {
    dispatch(updateReviewDraft({
      comment: {
        ...reviewDraft.comment,
        text: e.target.value
      }
    }));
  }, [dispatch, reviewDraft.comment]);

  // Handle photos updated
  const handlePhotosUpdated = useCallback((photos) => {
    dispatch(updateReviewDraft({ pictures: photos }));
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('You must be logged in to leave a review');
      return;
    }

    if (!paramProductId && !product) {
      toast.error('Product information is missing');
      return;
    }

    if (!reviewDraft.comment?.text || reviewDraft.comment.text.trim().length < 10) {
      toast.error('Please write a review with at least 10 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      const isUpdating = !!reviewDraft.reviewId || !!location.state?.existingReview?.id;
      
      const finalFulfillmentGroupId = fulfillmentGroupId || reviewDraft.fulfillmentGroupId || location.state?.fulfillmentGroupId;
      const orderId = reviewDraft.orderId || location.state?.orderId;
      const merchantId = reviewDraft.merchantId || location.state?.merchantId;
      const orderItemData = location.state?.orderItemData;
      const reviewId = reviewDraft.reviewId || location.state?.existingReview?.id;
      
      const reviewData = {
        rating: reviewDraft.rating,
        comment: reviewDraft.comment?.text || '',
        products: (paramProductId || product?.id) ? [parseInt(paramProductId || product.id, 10)] : [],
        customers: [parseInt(user.id, 10)],
        merchants: merchantId ? [parseInt(merchantId, 10)] : [],
        orders: orderId ? [parseInt(orderId, 10)] : [],
        fulfillmentGroup: finalFulfillmentGroupId ? parseInt(finalFulfillmentGroupId, 10) : null,
        orderItem: orderItemData?.id || undefined,
        pictures: reviewDraft.pictures?.map(pic => pic.id) || [],
        isReviewCustomer: false,
        isReviewProduct: true
      };
      
      let result;
      if (isUpdating && reviewId) {
        result = await updateReview({ 
          id: reviewId, 
          ...reviewData 
        }).unwrap();
      } else {
        result = await addReview(reviewData).unwrap();
      }
      
      toast.success(isUpdating ? 'Review updated successfully!' : 'Review submitted successfully!');

      if (paramProductId) {
        history.push(`/product/${paramProductId}`);
      } else {
        history.goBack();
      }
    } catch (err) {
      console.error('Failed to submit review:', err);
      toast.error(err.data?.message || err.message || 'Failed to submit review');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-6 pb-4 border-b">
          <h1 className="text-2xl font-bold text-gray-800">
            {location.state?.existingReview ? 'Edit Your Review' : 'Leave a Review'}
          </h1>
          <Button
            variant="outline"
            onClick={() => history.goBack()}
            disabled={isSubmitting}
            className="flex items-center gap-2"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </Button>
        </div>
        
        {/* Product Info */}
        {product && (
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <div className="flex items-center gap-4">
              {product.photos && product.photos[0] && (
                <img 
                  src={product.photos[0].url}
                  alt={product.name}
                  className="w-20 h-20 rounded-md object-cover border border-gray-200"
                />
              )}
              <div>
                <h2 className="font-semibold text-lg">{product.name}</h2>
                {product.brand && <p className="text-sm text-gray-600">Brand: {product.brand}</p>}
                {product.price && <p className="text-sm font-medium">${product.price.toFixed(2)}</p>}
              </div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Rating selector */}
          <div className="bg-gray-50 p-5 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-3">Your Rating</label>
            <div className="flex items-center justify-center sm:justify-start">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className="p-1 focus:outline-none transition-transform hover:scale-110"
                  onClick={() => handleRatingChange(star)}
                >
                  <StarIcon
                    className={`h-10 w-10 ${
                      star <= reviewDraft.rating ? 'text-yellow-400' : 'text-gray-300'
                    } transition-colors duration-200`}
                  />
                </button>
              ))}
              <span className="ml-4 text-sm font-medium text-gray-700">
                {reviewDraft.rating} {reviewDraft.rating === 1 ? 'Star' : 'Stars'}
              </span>
            </div>
          </div>
          
          {/* Review text */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Your Review
              </label>
              <span className={`text-xs ${reviewDraft.comment?.text?.length < 10 && reviewDraft.comment?.text?.length > 0 ? 'text-red-500' : 'text-gray-500'}`}>
                {(reviewDraft.comment?.text?.length || 0)}/1000 characters {reviewDraft.comment?.text?.length < 10 && reviewDraft.comment?.text?.length > 0 && '(minimum 10 characters required)'}
              </span>
            </div>
            <FormInput
              as="textarea"
              rows={8}
              name="review-text"
              value={reviewDraft.comment?.text || ""}
              onChange={handleCommentChange}
              placeholder="Share your experience with this product. What did you like or dislike? How was the quality and fit? Would you recommend it to others?"
              required
              maxLength={1000}
              className={`w-full ${reviewDraft.comment?.text?.length < 10 && reviewDraft.comment?.text?.length > 0 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
            />
            {reviewDraft.comment?.text?.length < 10 && reviewDraft.comment?.text?.length > 0 && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                Please write at least 10 characters for your review
              </p>
            )}
          </div>
          
          {/* Image upload section */}
          <div className="border border-gray-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Add Photos (Optional)
            </label>
            
            <ReviewPhotoUploader 
              onUploadPics={handlePhotosUpdated}
              error={null}
              initialPhotos={reviewDraft.pictures || []}
            />
          </div>
          
          {/* Submit button */}
          <div className="flex items-center justify-end pt-4 border-t">
            <Button
              variant="secondary"
              className="mr-3"
              onClick={() => history.goBack()}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || !reviewDraft.comment?.text || reviewDraft.comment?.text.length < 10}
              isLoading={isSubmitting}
            >
              {location.state?.existingReview ? 'Update Review' : 'Submit Review'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SingleItemReviewForm; 